/**
 * Reusable Sidebar Component
 * Modular sidebar that different pages can configure with different menu options
 */

import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { formatRelativeTime } from '@/utils/relativeTime';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@modules/auth';
import { useLanguage } from '@/hooks/useLanguage';
import { useFeature } from '@/hooks/useFeature';
import { useTenantPath } from '@/routing';
import { ProjectIcon } from '@modules/projects/components/ProjectIcon';
import { botService } from '@modules/chatbots';
import { conversationService } from '@modules/conversations';
import { ConfirmModal } from '@/components/common';
import { bidiIsolate } from '@/utils/bidi';
import { useBrand, useBrandAssets } from '@/brand';
import { openSupportPanel, useSupportRailEntry } from './supportWidget';
import { SupportIcon } from './SupportIcon';
import { BotAvatar } from '@/components/common/BotAvatar';
import { getFirstInitial, capitalizeName } from '@/utils/getInitials';
import brijIconBlack from '@/brand-assets/brij/icon-black.svg';
// Full wordmark shown when the sidebar is expanded. Single asset — inverted
// via CSS filter in dark mode so it reads on a dark background.

import { getAgentColor, markBaseFamily, type TextureColor } from '@/brand/textures';

// Hex colors for the small recent-chat dot — exact M² brandbook values.
// Keys mirror TextureColor so the dot always matches the agent's avatar color.
const RECENT_CHAT_DOT_HEX: Record<TextureColor, string> = {
  blue: '#6CC7EF',
  green: '#8BBF2A',
  red: '#D50032',
  yellow: '#FAB600',
};

/**
 * Resolve the recent-chat dot color for a conversation row.
 * Priority:
 *   1. forceAgentColor (grouped view — all rows under the same agent)
 *   2. Live bot record's explicit avatar_color (matches a manually chosen color)
 *   3. avatar_color cached on the conversation itself
 *   4. Deterministic hash from the bot name (same function BotAvatar uses) —
 *      guarantees the dot matches the textured avatar even with no backend data.
 */
function getRecentChatDotColor(
  conv: { chatbot_id?: string | null; chatbot_name?: string | null; avatar_color?: string | null } & Record<string, any>,
  botColorById: Record<string, { color?: string; variant?: number }>,
  forceAgentColor?: string,
): string {
  const raw =
    forceAgentColor ||
    (conv.chatbot_id ? botColorById[conv.chatbot_id]?.color : undefined) ||
    (conv as any).avatar_color ||
    (conv.chatbot_name ? getAgentColor(conv.chatbot_name) : undefined);
  // A Brij-mark colour (stored by the picker of a brand without avatar sets) is its own dot.
  if (markBaseFamily(raw)) return (raw as string).trim().toUpperCase();
  const key = (raw as TextureColor) in RECENT_CHAT_DOT_HEX ? (raw as TextureColor) : 'blue';
  return RECENT_CHAT_DOT_HEX[key];
}

import './Sidebar.css';
import { ConversationExportMenuItems } from '@modules/conversations/components/ConversationExportMenu';
import { useStayInViewport } from '@/hooks/useStayInViewport';

import { PUBLIC_DOCUMENTS_PUBLISHED } from '@modules/legal/publicPaths';
export interface SidebarNavItem {
  id: string;
  icon: React.ReactNode;
  label: string;
  tooltip: string;
  action: () => void;
  active?: boolean;
  requireAdmin?: boolean;
  requireSuperAdmin?: boolean;
  customClass?: string;
}

export interface SidebarListItem {
  id: string;
  label: string;
  sublabel?: string;
  active?: boolean;
  onClick: () => void;
  onDelete?: () => void;
}

export interface SidebarOptions {
  showHome?: boolean;
  showChat?: boolean;
  showBots?: boolean;
  showSearch?: boolean;
  showAdmin?: boolean;
  showGroupAdmin?: boolean;
  showSuperAdmin?: boolean;
  showConversations?: boolean;
  showBotsList?: boolean;
  customNavItems?: SidebarNavItem[];
  customListTitle?: string;
  customListItems?: SidebarListItem[];
  activeItem?: string;
  activeConversationId?: string | null;
  /**
   * Render the rail as a PICTURE of itself: the same markup and the same
   * brand mark, but no fetches.
   *
   * The White-Label editor mounts this sidebar so a super admin can see
   * another tenant's logo and nav exactly as that tenant's people do. The
   * conversations and agents it would otherwise load belong to the ADMIN,
   * not to the previewed tenant, so fetching them would be both wasteful
   * and misleading.
   */
  preview?: boolean;
}

interface SidebarProps {
  options?: SidebarOptions;
}

interface AgentEntry {
  chatbot_id: string | null;
  chatbot_name: string | null;
  avatar_color?: string | null;    // 'blue' | 'green' | 'red' | 'yellow'
  avatar_variant?: number | null;
  last_activity: string | null;
  conversation_count: number;
}

interface AgentGroupData {
  conversations: any[];
  page: number;
  hasNext: boolean;
  loaded: boolean;
  loading: boolean;
}

// Per-tab (sessionStorage) AND per-user cache. Including the user_id
// in the key guarantees the cache CANNOT serve another user's recent
// conversations even if it's somehow not wiped on logout.
const SIDEBAR_CACHE_PREFIX = 'sidebar_recent_chats:';

function sidebarCacheKey(): string {
  try {
    const raw = sessionStorage.getItem('user');
    const uid = raw ? (JSON.parse(raw)?.user_id || 'anon') : 'anon';
    return SIDEBAR_CACHE_PREFIX + uid;
  } catch {
    return SIDEBAR_CACHE_PREFIX + 'anon';
  }
}

// Helper to get cached data (used for initial state)
const getCachedData = (): { conversations: any[]; page: number; hasNext: boolean } => {
  try {
    const cached = sessionStorage.getItem(sidebarCacheKey());
    if (cached) {
      const parsed = JSON.parse(cached);
      const data = parsed.data || parsed;
      if (Array.isArray(data)) {
        return { conversations: data, page: parsed.page || 1, hasNext: parsed.hasNext ?? false };
      }
    }
  } catch {
    // Ignore cache errors
  }
  return { conversations: [], page: 1, hasNext: false };
};

const CONVERSATIONS_PER_PAGE = 35;
const GROUP_LOAD_SIZE = 20;

export const Sidebar: React.FC<SidebarProps> = ({ options = {} }) => {
  const navigate = useNavigate();
  // useTenantPath() now falls back to the signed-in user's
  // company_tenant_code when the URL itself has no slug — so the
  // sidebar's tenant-scoped items (Home, Agents, Admin, Insights,
  // etc.) are always clickable, even on /super-admin or
  // /design-system. The handlers always route to a real tenant URL.
  const tenantPath = useTenantPath();
  const location = useLocation();
  const { user, isAdmin, isGroupAdmin, isSuperAdmin } = useAuth();
  const { t } = useLanguage();
  const brand = useBrand();
  // anniversaryIcon: temporary 10th-anniversary mark, falls back to the
  // normal icon everywhere it isn't set (see brand/types.ts).
  const { anniversaryIcon: railIcon } = useBrandAssets();

  const flowsEnabled = useFeature('modules.flows');
  const agentsEnabled = useFeature('modules.agents');
  const journeyEnabled = useFeature('modules.journey');
  // The Library is its own module: a tenant sees the entry only when a
  // super admin has switched it on for them.
  const libraryEnabled = useFeature('modules.library');

  const insightsEnabled = useFeature('modules.insights');
  const projectsEnabled = useFeature('modules.projects');

  // Cost Simulator + Agent of Agents are no longer sidebar items (moved into
  // Settings / Agents pages), so their feature flags are read in those pages.

  // Sidebar starts collapsed on desktop; expands on hover, collapses on
  // mouse-leave. Mobile ignores this state (full width when opened).
  const [isPanelCollapsed, setIsPanelCollapsed] = useState(true);
  const [isMobileOpen, setIsMobileOpen] = useState(false);

  // Support and Feedback (PanLuma widget, see supportWidget.ts). On mobile the
  // drawer is closed first, as navigating does — and the panel opens only once
  // it has slid away, because opening it starts the screenshot capture, which
  // would otherwise catch the drawer half-way out over the page being reported.
  // A second tap during the slide is ignored: it would take the desktop path
  // (the drawer already reads as closed) and the delayed open would then
  // re-open the panel over what the person had started typing.
  const supportOpening = useRef(false);
  const openSupport = useCallback(() => {
    if (supportOpening.current) return;
    if (!isMobileOpen) {
      openSupportPanel(user);
      return;
    }
    supportOpening.current = true;
    const drawer = document.getElementById('sidebar');
    let fallbackTimer = 0;
    const open = () => {
      if (!supportOpening.current) return;
      supportOpening.current = false;
      window.clearTimeout(fallbackTimer);
      drawer?.removeEventListener('transitionend', onSlid);
      openSupportPanel(user);
    };
    const onSlid = (e: TransitionEvent) => {
      if (e.target === drawer && e.propertyName === 'transform') open();
    };
    drawer?.addEventListener('transitionend', onSlid);
    // No transitionend without a transition (reduced motion): the slide is 0.3s.
    fallbackTimer = window.setTimeout(open, 400);
    setIsMobileOpen(false);
  }, [isMobileOpen, user]);
  useSupportRailEntry();

  // Only react to hover on desktop — avoids state churn on touch devices
  // where hover events fire unexpectedly after a tap.
  const isDesktop = () => typeof window !== 'undefined' && window.innerWidth > 768;
  const handleSidebarMouseEnter = () => { if (isDesktop()) setIsPanelCollapsed(false); };
  const handleSidebarMouseLeave = () => { if (isDesktop()) setIsPanelCollapsed(true); };

  // Initialize from cache immediately to prevent flicker
  const cachedData = useRef(getCachedData());
  const [conversations, setConversations] = useState<any[]>(cachedData.current.conversations);
  const [bots, setBots] = useState<any[]>([]);

  // Lookup: chatbot_id → agent color/variant, used to color the Recent Chats dot
  // even when the conversation row doesn't carry its own avatar_color.
  const botColorById = useMemo(() => {
    const map: Record<string, { color?: string; variant?: number }> = {};
    for (const b of bots) {
      if (b?.chatbot_id) {
        map[b.chatbot_id] = { color: b.avatar_color, variant: b.avatar_variant };
      }
    }
    return map;
  }, [bots]);
  const [showUserDropdown, setShowUserDropdown] = useState(false);
  const [panelSearchOpen, setPanelSearchOpen] = useState(false);
  const [panelSearchQuery, setPanelSearchQuery] = useState('');
  const hasFetchedRef = useRef(false);

  // Pagination state
  const [globalPage, setGlobalPage] = useState(cachedData.current.page);
  const [globalHasNext, setGlobalHasNext] = useState(cachedData.current.hasNext);
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  // View toggle: flat (chronological) vs grouped (by agent)
  const [viewMode, setViewMode] = useState<'flat' | 'grouped'>('flat');
  const [openGroups, setOpenGroups] = useState<Set<string>>(new Set());

  // Grouped view: agent list + per-agent lazy-loaded conversations
  const [agentList, setAgentList] = useState<AgentEntry[]>([]);
  const [agentPage, setAgentPage] = useState(1);
  const [agentHasNext, setAgentHasNext] = useState(false);
  const [agentListLoading, setAgentListLoading] = useState(false);
  const [agentConvos, setAgentConvos] = useState<Map<string, AgentGroupData>>(new Map());
  const agentListFetchedRef = useRef(false);

  // Infinite scroll sentinel
  const scrollSentinelRef = useRef<HTMLDivElement>(null);

  // Conversation menu state
  const [menuOpenId, setMenuOpenId] = useState<string | null>(null);
  const [renamingId, setRenamingId] = useState<string | null>(null);
  const [renameValue, setRenameValue] = useState('');
  const [deleteModal, setDeleteModal] = useState<{
    isOpen: boolean;
    conversationId: string;
    title: string;
    loading: boolean;
    branchCount: number;
  }>({
    isOpen: false,
    conversationId: '',
    title: '',
    loading: false,
    branchCount: 0,
  });
  const renameInputRef = useRef<HTMLInputElement>(null);

  // Typing animation state for conversation titles
  const [animatingTitleId, setAnimatingTitleId] = useState<string | null>(null);
  const [animatingTitle, setAnimatingTitle] = useState<string>('');
  const animationTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  // Track newly created conversation for slide-in animation
  const [newConversationId, setNewConversationId] = useState<string | null>(null);

  // Branch popup state
  const [branchPopupId, setBranchPopupId] = useState<string | null>(null);
  const [branchPopupPos, setBranchPopupPos] = useState<{ top: number; left: number }>({ top: 0, left: 0 });

  // Menu dropdown position (for fixed positioning)
  const [menuPosition, setMenuPosition] = useState<{ top: number; left: number }>({ top: 0, left: 0 });
  // A row low in the list opens its menu near the window's edge, and
  // "Export conversation" grows it in place: keep it on screen.
  const menuDropdownRef = useStayInViewport<HTMLDivElement>();

  // Split screen: track the right panel's conversation ID for active highlighting
  const [splitScreenConversationId, setSplitScreenConversationId] = useState<string | null>(null);

  // ===== Grouped view: fetch agent list when entering grouped mode =====
  const fetchAgentList = useCallback(async (page: number = 1) => {
    setAgentListLoading(true);
    try {
      const result = await conversationService.getConversationAgents(page, 15);
      if (page === 1) {
        setAgentList(result.agents);
      } else {
        setAgentList(prev => {
          const existingKeys = new Set(prev.map(a => a.chatbot_id || 'general'));
          const newAgents = result.agents.filter(a => !existingKeys.has(a.chatbot_id || 'general'));
          return [...prev, ...newAgents];
        });
      }
      setAgentPage(result.pagination.page);
      setAgentHasNext(result.pagination.has_next);
    } catch (error) {
      console.error('[Sidebar] Failed to fetch agent list:', error);
    } finally {
      setAgentListLoading(false);
    }
  }, []);

  // Fetch agent list when switching to grouped view
  useEffect(() => {
    if (viewMode === 'grouped' && !agentListFetchedRef.current) {
      agentListFetchedRef.current = true;
      fetchAgentList(1);
    }
  }, [viewMode, fetchAgentList]);

  // Reset agent fetched flag when switching back to flat
  useEffect(() => {
    if (viewMode === 'flat') {
      agentListFetchedRef.current = false;
    }
  }, [viewMode]);

  // ===== Fetch conversations for a specific agent when opening the group =====
  const fetchAgentConversations = useCallback(async (agentKey: string, chatbotId: string | null, page: number = 1) => {
    const filterChatbotId = chatbotId === null ? 'none' : chatbotId;

    // Mark loading
    setAgentConvos(prev => {
      const next = new Map(prev);
      const existing = next.get(agentKey) || { conversations: [], page: 0, hasNext: false, loaded: false, loading: false };
      next.set(agentKey, { ...existing, loading: true });
      return next;
    });

    try {
      const result = await conversationService.getConversations(page, GROUP_LOAD_SIZE, filterChatbotId);

      setAgentConvos(prev => {
        const next = new Map(prev);
        const existing = next.get(agentKey) || { conversations: [], page: 0, hasNext: false, loaded: false, loading: false };

        let convos: any[];
        if (page === 1) {
          convos = result.conversations;
        } else {
          const existingIds = new Set(existing.conversations.map(c => c.conversation_id));
          const newOnes = result.conversations.filter(c => !existingIds.has(c.conversation_id));
          convos = [...existing.conversations, ...newOnes];
        }

        next.set(agentKey, {
          conversations: convos,
          page: result.pagination.page,
          hasNext: result.pagination.has_next,
          loaded: true,
          loading: false,
        });
        return next;
      });
    } catch (error) {
      console.error(`[Sidebar] Failed to fetch conversations for agent ${agentKey}:`, error);
      setAgentConvos(prev => {
        const next = new Map(prev);
        const existing = next.get(agentKey) || { conversations: [], page: 0, hasNext: false, loaded: false, loading: false };
        next.set(agentKey, { ...existing, loading: false });
        return next;
      });
    }
  }, []);

  // ===== Cache helper =====
  const updateCache = useCallback((convos: any[], page: number, hasNext: boolean) => {
    try {
      sessionStorage.setItem(sidebarCacheKey(), JSON.stringify({
        data: convos.slice(0, 100), // keep top 100 for instant load
        page,
        hasNext,
        timestamp: Date.now()
      }));
    } catch (e) {
      console.error('[Sidebar] Cache update failed:', e);
    }
  }, []);

  // ===== Infinite scroll: load next page =====
  const loadNextPage = useCallback(async () => {
    if (isLoadingMore || !globalHasNext) return;

    setIsLoadingMore(true);
    try {
      const result = await conversationService.getConversations(globalPage + 1, CONVERSATIONS_PER_PAGE);
      const newConvos = result.conversations;

      setConversations(prev => {
        const existingIds = new Set(prev.map(c => c.conversation_id));
        const deduped = newConvos.filter(c => !existingIds.has(c.conversation_id));
        const updated = [...prev, ...deduped];
        updateCache(updated, result.pagination.page, result.pagination.has_next);
        return updated;
      });

      setGlobalPage(result.pagination.page);
      setGlobalHasNext(result.pagination.has_next);
    } catch (error) {
      console.error('[Sidebar] Failed to load next page:', error);
    } finally {
      setIsLoadingMore(false);
    }
  }, [isLoadingMore, globalHasNext, globalPage, updateCache]);

  // ===== Load more agents for grouped view =====
  const loadMoreAgents = useCallback(async () => {
    if (agentListLoading || !agentHasNext) return;
    fetchAgentList(agentPage + 1);
  }, [agentListLoading, agentHasNext, agentPage, fetchAgentList]);

  // ===== IntersectionObserver for infinite scroll (both modes) =====
  useEffect(() => {
    const sentinel = scrollSentinelRef.current;
    if (!sentinel) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (!entries[0].isIntersecting) return;
        if (viewMode === 'flat') {
          if (globalHasNext && !isLoadingMore) loadNextPage();
        } else {
          if (agentHasNext && !agentListLoading) loadMoreAgents();
        }
      },
      { threshold: 0.1 }
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [viewMode, globalHasNext, isLoadingMore, loadNextPage, agentHasNext, agentListLoading, loadMoreAgents]);

  useEffect(() => {
    const handleSplitScreenChanged = (event: CustomEvent<{ conversationId: string | null }>) => {
      setSplitScreenConversationId(event.detail.conversationId || null);
    };
    window.addEventListener('splitScreenChanged', handleSplitScreenChanged as EventListener);
    return () => {
      window.removeEventListener('splitScreenChanged', handleSplitScreenChanged as EventListener);
    };
  }, []);

  // Listen for conversation activity (new message sent) - moves conversation to top or adds it
  useEffect(() => {
    const handleConversationActivity = (event: CustomEvent<{ conversationId: string; conversationData?: any }>) => {
      const { conversationId, conversationData } = event.detail;

      // Skip internal bot conversations (e.g. Insights Agent) — they have their own sidebar
      if (conversationData?.is_internal) return;

      setConversations(prev => {
        const index = prev.findIndex(c => c.conversation_id === conversationId);

        let updated: typeof prev;

        if (index >= 0) {
          const conv = { ...prev[index], last_message_at: new Date().toISOString() };
          updated = [conv, ...prev.slice(0, index), ...prev.slice(index + 1)];
        } else if (conversationData) {
          // Flow conversations dedupe by flow_id (most recent wins) so the
          // same flow doesn't appear multiple times in Recent Chats.
          const newFlowId = (conversationData as any).conversation_context?.flow_id;
          if (newFlowId) {
            updated = [conversationData, ...prev.filter((c: any) => c.conversation_context?.flow_id !== newFlowId)];
          } else {
            updated = [conversationData, ...prev];
          }
        } else {
          return prev;
        }

        // If this conversation is a branch, also move its parent to the top
        const activeConv = updated.find(c => c.conversation_id === conversationId);
        const parentId = activeConv?.parent_conversation_id;
        if (parentId) {
          const parentIndex = updated.findIndex(c => c.conversation_id === parentId);
          if (parentIndex > 0) {
            const parent = { ...updated[parentIndex], last_message_at: new Date().toISOString() };
            updated = [parent, ...updated.slice(0, parentIndex), ...updated.slice(parentIndex + 1)];
          }
        }

        updateCache(updated, globalPage, globalHasNext);
        return updated;
      });
    };

    window.addEventListener('conversationActivity', handleConversationActivity as EventListener);
    return () => {
      window.removeEventListener('conversationActivity', handleConversationActivity as EventListener);
    };
  }, [globalPage, globalHasNext, updateCache]);

  // Listen for branchCreated events
  useEffect(() => {
    const handleBranchCreated = (event: CustomEvent<{ result: any }>) => {
      const { result } = event.detail;
      if (!result?.conversation) return;

      const branchConv = result.conversation;
      const parentId = branchConv.parent_conversation_id;

      setConversations(prev => {
        const exists = prev.some(c => c.conversation_id === branchConv.conversation_id);
        let updated = exists ? prev : [branchConv, ...prev];

        if (parentId) {
          updated = updated.map(c =>
            c.conversation_id === parentId
              ? { ...c, branch_count: (c.branch_count || 0) + (exists ? 0 : 1) }
              : c
          );
        }

        updateCache(updated, globalPage, globalHasNext);
        return updated;
      });
    };

    window.addEventListener('branchCreated', handleBranchCreated as EventListener);
    return () => {
      window.removeEventListener('branchCreated', handleBranchCreated as EventListener);
    };
  }, [globalPage, globalHasNext, updateCache]);

  // Listen for title updates from ChatPage
  useEffect(() => {
    const handleTitleUpdate = (event: CustomEvent<{ conversationId: string; title: string; chatbotName?: string; isInternal?: boolean }>) => {
      const { conversationId, title, chatbotName, isInternal } = event.detail;

      // Skip internal bot conversations (e.g. Insights Agent)
      if (isInternal) return;

      if (animationTimeoutRef.current) {
        clearTimeout(animationTimeoutRef.current);
        animationTimeoutRef.current = null;
      }

      setConversations(prev => {
        const existingIndex = prev.findIndex(c => c.conversation_id === conversationId);
        if (existingIndex < 0) {
          setNewConversationId(conversationId);
          setTimeout(() => setNewConversationId(null), 2000);
          return [{ conversation_id: conversationId, conversation_title: '', chatbot_name: chatbotName, created_at: new Date().toISOString() } as any, ...prev];
        }
        return prev;
      });

      setAnimatingTitleId(conversationId);
      setAnimatingTitle(title.charAt(0));

      let currentIndex = 1;
      const typeNextChar = () => {
        if (currentIndex < title.length) {
          setAnimatingTitle(title.substring(0, currentIndex + 1));
          currentIndex++;
          animationTimeoutRef.current = setTimeout(typeNextChar, 40);
        } else {
          setConversations(prev => {
            const existingIndex = prev.findIndex(c => c.conversation_id === conversationId);
            if (existingIndex >= 0) {
              const updated = [...prev];
              updated[existingIndex] = { ...updated[existingIndex], conversation_title: title };
              return updated;
            }
            return prev;
          });

          animationTimeoutRef.current = setTimeout(() => {
            setAnimatingTitleId(null);
            setAnimatingTitle('');
          }, 100);
        }
      };

      animationTimeoutRef.current = setTimeout(typeNextChar, 50);
    };

    window.addEventListener('conversationTitleUpdated', handleTitleUpdate as EventListener);
    return () => {
      window.removeEventListener('conversationTitleUpdated', handleTitleUpdate as EventListener);
      if (animationTimeoutRef.current) {
        clearTimeout(animationTimeoutRef.current);
        animationTimeoutRef.current = null;
      }
    };
  }, []);

  // Close mobile sidebar when navigating
  useEffect(() => {
    setIsMobileOpen(false);
  }, [location.pathname]);

  const handleOverlayClick = () => {
    setIsMobileOpen(false);
    const sidebar = document.getElementById('sidebar');
    const overlay = document.querySelector('.sidebar-overlay');
    sidebar?.classList.remove('mobile-open');
    overlay?.classList.remove('visible');
  };

  const handleMobileMenuToggle = () => {
    setIsMobileOpen(!isMobileOpen);
  };

  const closeMobileSidebar = () => {
    if (window.innerWidth <= 768) {
      setIsMobileOpen(false);
      const sidebar = document.getElementById('sidebar');
      const overlay = document.querySelector('.sidebar-overlay');
      sidebar?.classList.remove('mobile-open');
      overlay?.classList.remove('visible');
    }
  };

  // Force consistent sidebar options
  const defaultOptions: SidebarOptions = {
    showHome: true,
    showChat: false,
    showBots: true,
    showSearch: true,
    showAdmin: true,
    showGroupAdmin: false,
    showSuperAdmin: false,
    showConversations: true,
    showBotsList: false,
    customNavItems: options.customNavItems || [],
    customListTitle: options.customListTitle,
    customListItems: options.customListItems,
    activeItem: options.activeItem || 'home',
    preview: options.preview || false,
    activeConversationId: options.activeConversationId,
  };

  // ===== Icon components - compact GPT-style =====
  const icons = {
    menu: (
      <svg className="icon" viewBox="0 0 24 24">
        <path d="M3 12h18M3 6h18M3 18h18" stroke="currentColor" strokeWidth="2" fill="none" />
      </svg>
    ),
    home: (
      <svg className="icon" viewBox="0 0 24 24">
        <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    ),
    newChat: (
      <svg className="icon" viewBox="0 0 24 24">
        <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    ),
    search: (
      <svg className="icon" viewBox="0 0 24 24">
        <circle cx="11" cy="11" r="8" stroke="currentColor" strokeWidth="2" fill="none" />
        <path d="M21 21l-4.35-4.35" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      </svg>
    ),
    bots: (
      // Bot/agent icon - friendly robot face
      <svg className="icon" viewBox="0 0 24 24">
        <rect x="3" y="8" width="18" height="12" rx="3" stroke="currentColor" strokeWidth="2" fill="none" />
        <circle cx="9" cy="14" r="1.5" fill="currentColor" />
        <circle cx="15" cy="14" r="1.5" fill="currentColor" />
        <path d="M12 2v4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
        <circle cx="12" cy="2" r="1" fill="currentColor" />
        <path d="M1 14h2M21 14h2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      </svg>
    ),
    flows: (
      // Flow / lesson icon — open book
      <svg className="icon" viewBox="0 0 24 24">
        <path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    ),
    admin: (
      // Users/people management icon
      <svg className="icon" viewBox="0 0 24 24">
        <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round" />
        <circle cx="9" cy="7" r="4" stroke="currentColor" strokeWidth="2" fill="none" />
        <path d="M23 21v-2a4 4 0 0 0-3-3.87" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M16 3.13a4 4 0 0 1 0 7.75" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    ),
    skills: (
      // Graduation cap icon
      <svg className="icon" viewBox="0 0 24 24">
        <path d="M22 10l-10-5-10 5 10 5 10-5z" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M6 12v5c0 1.66 2.69 3 6 3s6-1.34 6-3v-5" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M22 10v6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      </svg>
    ),
    dataBundles: (
      <svg className="icon" viewBox="0 0 24 24">
        <ellipse cx="12" cy="5" rx="9" ry="3" stroke="currentColor" strokeWidth="2" fill="none" />
        <path d="M21 12c0 1.66-4 3-9 3s-9-1.34-9-3" stroke="currentColor" strokeWidth="2" fill="none" />
        <path d="M3 5v14c0 1.66 4 3 9 3s9-1.34 9-3V5" stroke="currentColor" strokeWidth="2" fill="none" />
      </svg>
    ),
    vault: (
      // Folder tree / drive icon for The Vault
      <svg className="icon" viewBox="0 0 24 24">
        <path d="M3 7a2 2 0 0 1 2-2h4l2 2h8a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" stroke="currentColor" strokeWidth="2" fill="none" strokeLinejoin="round" />
      </svg>
    ),
    // A board with work on it, NOT a folder — the folder above is the Vault's
    // mark, and two entries in the same rail must not share a glyph.
    // See ProjectIcon.tsx.
    projects: <ProjectIcon className="icon" strokeWidth={1.9} />,
    insights: (
      // Bar chart / analytics icon
      <svg className="icon" viewBox="0 0 24 24">
        <line x1="18" y1="20" x2="18" y2="10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
        <line x1="12" y1="20" x2="12" y2="4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
        <line x1="6" y1="20" x2="6" y2="14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
        <line x1="2" y1="20" x2="22" y2="20" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      </svg>
    ),
    costSimulator: (
      // Calculator icon
      <svg className="icon" viewBox="0 0 24 24">
        <rect x="4" y="2" width="16" height="20" rx="2" stroke="currentColor" strokeWidth="2" fill="none" />
        <rect x="8" y="6" width="8" height="3" rx="1" stroke="currentColor" strokeWidth="1.5" fill="none" />
        <circle cx="8" cy="13" r="1" fill="currentColor" />
        <circle cx="12" cy="13" r="1" fill="currentColor" />
        <circle cx="16" cy="13" r="1" fill="currentColor" />
        <circle cx="8" cy="17" r="1" fill="currentColor" />
        <circle cx="12" cy="17" r="1" fill="currentColor" />
        <circle cx="16" cy="17" r="1" fill="currentColor" />
      </svg>
    ),
    agentOfAgents: (
      // Lab flask — erlenmeyer with a narrow neck, wide angled body,
      // a measurement mark, and two bubbles inside. Reads as "lab /
      // experiment / build" — the place where you build agents.
      <svg className="icon" viewBox="0 0 24 24">
        {/* Flask outline: neck → body → rounded bottom */}
        <path
          d="M 9 3 L 9 9 L 4.2 19.2 Q 3.7 20.5 5.2 20.5 L 18.8 20.5 Q 20.3 20.5 19.8 19.2 L 15 9 L 15 3"
          stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"
          strokeLinejoin="round" fill="none"
        />
        {/* Top rim */}
        <line x1="8" y1="3" x2="16" y2="3"
              stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
        {/* Liquid line across the body */}
        <line x1="6.5" y1="16" x2="17.5" y2="16"
              stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
        {/* Bubbles inside the liquid */}
        <circle cx="10" cy="18.2" r="0.9" fill="currentColor" />
        <circle cx="13.5" cy="17.5" r="0.7" fill="currentColor" />
      </svg>
    ),
    star: (
      <svg className="icon" viewBox="0 0 24 24">
        <path d="M12 1l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 1z" stroke="currentColor" strokeWidth="2" fill="none" />
      </svg>
    ),
    designSystem: (
      // Palette icon — paint swatch + dot grid
      <svg className="icon" viewBox="0 0 24 24">
        <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" fill="none" />
        <circle cx="8" cy="9" r="1.4" fill="currentColor" />
        <circle cx="16" cy="9" r="1.4" fill="currentColor" />
        <circle cx="8" cy="15" r="1.4" fill="currentColor" />
        <circle cx="16" cy="15" r="1.4" fill="currentColor" />
      </svg>
    ),
    filter: (
      <svg className="icon" viewBox="0 0 24 24" width="14" height="14">
        <path d="M12 3c2.755 0 5.455.232 8.083.678.533.09.917.556.917 1.096v1.044a2.25 2.25 0 0 1-.659 1.591l-5.432 5.432a2.25 2.25 0 0 0-.659 1.591v2.927a2.25 2.25 0 0 1-1.244 2.013L9.75 21v-6.568a2.25 2.25 0 0 0-.659-1.591L3.659 7.409A2.25 2.25 0 0 1 3 5.818V4.774c0-.54.384-1.006.917-1.096A48.38 48.38 0 0 1 12 3z" fill="currentColor" />
      </svg>
    ),
  };

  // Logo + Home nav always reset to the agent-picker view. Two cases:
  //   1. Already on '/' — Router won't remount MainPage, so we fire a custom
  //      event that MainPage listens for to reset its phase/state.
  //   2. Coming from a different route (e.g. '/chat?conversation=X') — passing
  //      `state: { reset: true }` tells MainPage to ignore the URL params it
  //      would otherwise re-hydrate selected agent / conversation from.
  //   `replace: true` so the back button doesn't return to the stale chat.
  const handleNavigateHome = () => {
    closeMobileSidebar();
    window.dispatchEvent(new CustomEvent('corpbrain:newChat'));
    // tenantPath always returns a real tenant URL (URL slug, or fallback
    // to user.company_tenant_code) — works whether we're on /super-admin,
    // /design-system, or already inside a tenant subtree.
    navigate(tenantPath('/'), { replace: true, state: { reset: true } });
  };
  // `replace: true` on tab-switch navs so back-button doesn't traverse
  // the user's nav clicks — they expect "back" to go to the previous
  // meaningful page (e.g. the conversation they were in), not "the
  // last sidebar tab I clicked."
  const handleNavigateBots = () => { navigate(tenantPath('/agents'), { replace: true }); };
  const handleNavigateFlows = () => { navigate(tenantPath('/flows'), { replace: true }); };
  const handleOpenAdmin = () => { navigate(tenantPath('/admin'), { replace: true }); };
  const handleOpenVault = () => { navigate(tenantPath('/library'), { replace: true }); };
  // Skills + Data Bundles are now tabs inside the Agents page (BotsPage), not
  // sidebar items — their nav handlers were removed with the nav items.
  // Super-admin pages live under the super_admin's OWN tenant
  // (/<home-tenant>/super-admin, /<home-tenant>/design-system) — never
  // under another tenant's brand. tenantPath would use the URL slug,
  // which could be a customer tenant if the super_admin is browsing
  // one; we want their home tenant specifically.
  const superAdminHomeSlug = (user?.company_tenant_code || user?.tenant_code || '').toLowerCase();
  // Active URL slug (used to hide Super Admin / Design System items
  // when the super_admin is visiting a customer tenant — those tools
  // are pinned to /<their-home>/super-admin and shouldn't tease from
  // another tenant's brand).
  const currentUrlSlug = (location.pathname.match(/^\/([^/]+)/)?.[1] || '').toLowerCase();
  const onSuperAdminHomeTenant = !!superAdminHomeSlug && currentUrlSlug === superAdminHomeSlug;
  const handleOpenSuperAdmin = () => { navigate(`/${superAdminHomeSlug}/super-admin`, { replace: true }); };
  // Design System is now a tab inside the Super Admin page — its sidebar nav
  // handler was removed with the item.
  const handleOpenInsights = () => { navigate(tenantPath('/insights'), { replace: true }); };
  const handleOpenProjects = () => { closeMobileSidebar(); navigate(tenantPath('/projects'), { replace: true }); };
  // Cost Simulator moved into the Settings page; Agent of Agents into the
  // Agents page — their sidebar nav handlers were removed with their items.

  // Conversation menu handlers
  const handleMenuToggle = (e: React.MouseEvent, conversationId: string) => {
    e.stopPropagation();
    e.preventDefault();
    if (menuOpenId === conversationId) {
      setMenuOpenId(null);
    } else {
      const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
      setMenuPosition({ top: rect.bottom + 4, left: rect.right - 160 });
      setMenuOpenId(conversationId);
    }
  };

  const handleStartRename = (e: React.MouseEvent, conv: any) => {
    e.stopPropagation();
    setMenuOpenId(null);
    setRenamingId(conv.conversation_id);
    setRenameValue(conv.conversation_title || '');
    setTimeout(() => renameInputRef.current?.select(), 0);
  };

  const handleRenameSubmit = async (conversationId: string) => {
    if (!renameValue.trim()) {
      setRenamingId(null);
      return;
    }

    try {
      await conversationService.updateConversationTitle(conversationId, renameValue.trim());
      setConversations(prev => {
        const updated = prev.map(c =>
          c.conversation_id === conversationId
            ? { ...c, conversation_title: renameValue.trim() }
            : c
        );
        updateCache(updated, globalPage, globalHasNext);
        return updated;
      });
    } catch (error) {
      console.error('[Sidebar] Failed to rename conversation:', error);
    }
    setRenamingId(null);
  };

  const handleRenameKeyDown = (e: React.KeyboardEvent, conversationId: string) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleRenameSubmit(conversationId);
    } else if (e.key === 'Escape') {
      setRenamingId(null);
    }
  };

  const handleOpenDeleteModal = (e: React.MouseEvent, conv: any) => {
    e.stopPropagation();
    setMenuOpenId(null);
    const branchCount = conv.branch_count || conversations.filter(
      (c: any) => c.parent_conversation_id === conv.conversation_id
    ).length;
    setDeleteModal({
      isOpen: true,
      conversationId: conv.conversation_id,
      title: conv.conversation_title || t('chat.untitledConversation'),
      loading: false,
      branchCount,
    });
  };

  const handleDeleteConversation = async () => {
    if (!deleteModal.conversationId) return;

    setDeleteModal(prev => ({ ...prev, loading: true }));

    try {
      // For flow conversations, also delete the flow progress
      const deletedConv = conversations.find(c => c.conversation_id === deleteModal.conversationId);
      const flowId = deletedConv?.conversation_context?.flow_id;
      if (flowId) {
        try {
          const { default: api } = await import('@/services/api');
          await api.delete(`/flows/flows/${flowId}/progress`);
        } catch { /* flow progress may already be gone */ }
      }

      await conversationService.deleteConversation(deleteModal.conversationId);
      const deletedId = deleteModal.conversationId;
      setConversations(prev => {
        const updated = prev.filter(c => c.conversation_id !== deletedId && c.parent_conversation_id !== deletedId);
        updateCache(updated, globalPage, globalHasNext);
        return updated;
      });
      // Navigate away if the deleted chat is currently visible
      const urlConvId = new URLSearchParams(location.search).get('conversation');
      if (splitScreenConversationId && (deletedId === urlConvId || deletedId === splitScreenConversationId)) {
        // Split screen: close the deleted panel, keep the surviving one full screen
        const survivingId = deletedId === urlConvId ? splitScreenConversationId : urlConvId;
        // Tell ChatPage to close split view
        window.dispatchEvent(new CustomEvent('closeSplitView'));
        if (survivingId) {
          navigate(tenantPath(`/chat?conversation=${survivingId}`), { replace: true });
        } else {
          navigate(tenantPath('/'));
        }
      } else if (flowId) {
        // When the deleted conversation was a flow, jump back to the flows dashboard.
        navigate(tenantPath('/flows'));
      } else if (location.search.includes(deletedId)) {
        navigate(tenantPath('/'));
      }
      setDeleteModal({ isOpen: false, conversationId: '', title: '', loading: false, branchCount: 0 });
    } catch (error) {
      console.error('[Sidebar] Failed to delete conversation:', error);
      setDeleteModal(prev => ({ ...prev, loading: false }));
    }
  };

  // Close conversation menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (menuOpenId && !target.closest('.conversation-menu-container')) {
        setMenuOpenId(null);
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [menuOpenId]);

  // Fetch conversations in background (only once per session)
  useEffect(() => {
    if (defaultOptions.preview) return;
    if (hasFetchedRef.current) return;
    hasFetchedRef.current = true;

    const fetchSidebarData = async () => {
      try {
        if (defaultOptions.showConversations) {
          const result = await conversationService.getConversations(1, CONVERSATIONS_PER_PAGE);
          let sortedConvos = result.conversations.sort((a, b) => {
            const dateA = new Date(a.last_message_at || a.created_at).getTime();
            const dateB = new Date(b.last_message_at || b.created_at).getTime();
            return dateB - dateA;
          });
          // Flow conversations dedupe by flow_id (most recent wins). Non-flow
          // conversations pass through unchanged so users with both modules
          // active see their full Recent Chats list.
          const seenFlows = new Set<string>();
          sortedConvos = sortedConvos.filter((c: any) => {
            const fid = c.conversation_context?.flow_id;
            if (!fid) return true;
            if (seenFlows.has(fid)) return false;
            seenFlows.add(fid);
            return true;
          });
          setConversations(sortedConvos);
          setGlobalPage(result.pagination.page);
          setGlobalHasNext(result.pagination.has_next);
          updateCache(sortedConvos, result.pagination.page, result.pagination.has_next);
        }

        // Always fetch bots so we can color Recent Chat dots by agent color,
        // regardless of whether the Bots List section is visible.
        const allBots = await botService.getBots();
        setBots(allBots);
      } catch (error) {
        console.error('[ERROR] Failed to fetch sidebar data:', error);
      }
    };

    fetchSidebarData();
  }, []);

  // Close user dropdown and branch popup when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (showUserDropdown && !target.closest('.user-section') && !target.closest('.sidebar-footer')) {
        setShowUserDropdown(false);
      }
      if (branchPopupId && !target.closest('.branch-popup') && !target.closest('.conversation-branch-badge')) {
        setBranchPopupId(null);
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, [showUserDropdown, branchPopupId]);

  // Keyboard shortcut for search (Ctrl/Cmd + K) — opens the inline panel search
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if ((event.ctrlKey || event.metaKey) && event.key === 'k') {
        event.preventDefault();
        setPanelSearchOpen(true);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  // Derive the active sidebar tab from the URL pathname. URLs follow
  // the path-based tenant model — `/<tenantSlug>/admin/skills` etc.
  // — so we strip the leading tenant slug (any non-reserved first
  // segment) and match the SUB-path. This way the active highlight
  // always tracks the URL, even when a parent (page) didn't pass
  // `options.activeItem` through.
  const getActiveItem = () => {
    // Same set as routing/reservedSlugs.ts — kept inline to avoid a
    // circular import (Sidebar is deeply imported, the routing
    // module pulls Sidebar transitively via TenantBoundary).
    const reserved = new Set([
      'login', 'super-admin', 'design-system', 'settings', 'terms', 'privacy',
      'admin', 'bots', 'chat', 'flows', 'insights', 'cost-simulator', 'logs',
      'skills', 'data-bundles', 'vault', 'agent-of-agents', 'projects',
      'api', 'health', 'static', 'assets', '_t', '_',
    ]);
    const segs = location.pathname.split('/').filter(Boolean);
    // Tenant-scoped: drop the first segment when it's the tenant slug.
    const sub = !reserved.has(segs[0] || '') ? segs.slice(1) : segs;
    const path = '/' + sub.join('/');
    if (sub.length === 0) return 'home';
    if (path.startsWith('/chat')) return 'home';
    if (path.startsWith('/learn')) return 'learn';
    if (path.startsWith('/agents')) return 'bots';
    if (path.startsWith('/flows')) return 'flows';
    if (path.startsWith('/projects')) return 'projects';
    if (path.startsWith('/insights')) return 'insights';
    if (path.startsWith('/cost-simulator')) return 'cost-simulator';
    // Reached via the Agents page tab; deep links highlight Agents.
    if (path.startsWith('/agent-of-agents')) return 'bots';
    if (path === '/skills' || path.startsWith('/skills/')) return 'skills';
    if (path === '/data-bundles' || path.startsWith('/data-bundles/')) return 'data-bundles';
    if (path === '/library' || path.startsWith('/library/')) return 'vault';
    if (path === '/super-admin' || path.startsWith('/super-admin/')) return 'superadmin';
    if (path === '/design-system') return 'designsystem';
    // Legacy paths (during the redirect grace window).
    if (path === '/admin/skills' || path.startsWith('/admin/skills/')) return 'skills';
    if (path === '/admin/data-bundles' || path.startsWith('/admin/data-bundles/')) return 'data-bundles';
    if (path.startsWith('/admin')) return 'admin';
    if (path === '/settings' || path.startsWith('/settings/')) return 'settings';
    return defaultOptions.activeItem;
  };

  const activeItem = getActiveItem();

  const getActiveConversationId = (): string | null => {
    if (options.activeConversationId) {
      return options.activeConversationId;
    }
    const searchParams = new URLSearchParams(location.search);
    return searchParams.get('conversation');
  };

  const activeConversationId = getActiveConversationId();

  const activeParentId = (() => {
    if (!activeConversationId) return null;
    const activeConv = conversations.find((c: any) => c.conversation_id === activeConversationId);
    return activeConv?.parent_conversation_id || null;
  })();

  const splitScreenParentId = (() => {
    if (!splitScreenConversationId) return null;
    const splitConv = conversations.find((c: any) => c.conversation_id === splitScreenConversationId);
    return splitConv?.parent_conversation_id || null;
  })();

  const isActiveConversation = (convId: string) =>
    convId === activeConversationId ||
    convId === activeParentId ||
    convId === splitScreenConversationId ||
    convId === splitScreenParentId;

  // ===== Shared conversation item renderer =====
  // `forceAgentColor` — when rendered inside a grouped view under a specific
  // agent, every conversation dot takes that agent's color (overrides any
  // cached value on the conversation row).
  const renderConversationItem = (conv: any, isBranch: boolean, branchMap: Map<string, any[]>, forceAgentColor?: string) => (
    <div
      key={conv.conversation_id}
      className={`conversation-item ${isBranch ? 'conversation-branch' : ''} ${renamingId === conv.conversation_id ? 'renaming' : ''} ${newConversationId === conv.conversation_id ? 'slide-in new-highlight' : ''} ${isActiveConversation(conv.conversation_id) ? 'active' : ''}`}
      onClick={() => {
        if (renamingId !== conv.conversation_id) {
          closeMobileSidebar();
          // Flow conversations → open in flow player instead of regular chat
          const flowId = (conv as any).conversation_context?.flow_id;
          if (flowId) {
            navigate(tenantPath(`/flows/flow/${flowId}`));
          } else {
            navigate(tenantPath(`/chat?conversation=${conv.conversation_id}`));
          }
        }
      }}
    >
      {renamingId === conv.conversation_id ? (
        <input dir="auto"
          ref={renameInputRef}
          type="text"
          className="conversation-rename-input"
          value={renameValue}
          onChange={(e) => setRenameValue(e.target.value)}
          onKeyDown={(e) => handleRenameKeyDown(e, conv.conversation_id)}
          onBlur={() => handleRenameSubmit(conv.conversation_id)}
          onClick={(e) => e.stopPropagation()}
          autoFocus
        />
      ) : (
        <>
          {isBranch && (conv as any).branch_kind === 'agent' && conv.chatbot_name ? (
            /* A handoff branch belongs to a DIFFERENT agent, so identify it by
               that agent — the generic fork glyph below says "this is a branch"
               but hides the one fact that matters here: who took it over. */
            <span
              className="recent-chat-dot conversation-branch-agent-dot"
              title={conv.chatbot_name}
              style={{ background: getRecentChatDotColor(conv, botColorById, forceAgentColor) }}
            />
          ) : isBranch ? (
            <span className="conversation-branch-icon" title={t('chat.branchLabel')}>
              <svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="18" r="3" />
                <circle cx="6" cy="6" r="3" />
                <circle cx="18" cy="6" r="3" />
                <path d="M18 9v1a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2V9" />
                <line x1="12" y1="12" x2="12" y2="15" />
              </svg>
            </span>
          ) : conv.chatbot_name ? (
            <span
              className="recent-chat-dot"
              title={conv.chatbot_name}
              style={{ background: getRecentChatDotColor(conv, botColorById, forceAgentColor) }}
            />
          ) : (
            <span className="recent-chat-icon recent-chat-icon-general" title={(conv as any).conversation_context?.flow_id ? 'Flow' : t('sidebar.generalAI')}>
              <svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                {(conv as any).conversation_context?.flow_id ? (
                  <path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z"/>
                ) : (
                  <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
                )}
              </svg>
            </span>
          )}
          <div dir="auto" className={`conversation-title ${animatingTitleId === conv.conversation_id ? 'typing-animation' : ''}`}>
            {animatingTitleId === conv.conversation_id
              ? animatingTitle
              : ((conv as any).conversation_context?.flow_id
                  ? (conv.conversation_title || '').replace(/^Flow:\s*/i, '') || 'Untitled'
                  : (conv.conversation_title || t('chat.untitledConversation')))}
            {animatingTitleId === conv.conversation_id && <span className="typing-cursor">|</span>}
          </div>
          {/* Which agent this branch is with. Sibling branches of one thread
              share a title, so the agent name is the only thing that tells
              them apart in the list. */}
          {isBranch && (conv as any).branch_kind === 'agent' && conv.chatbot_name && (
            <span className="conversation-branch-agent" title={conv.chatbot_name} dir="auto">
              {conv.chatbot_name}
            </span>
          )}
          {/* Branch count badge for parent conversations */}
          {!isBranch && (branchMap.get(conv.conversation_id)?.length || (conv.branch_count > 0 && !branchMap.has(conv.conversation_id))) && (
            <button
              className="conversation-branch-badge"
              onClick={(e) => {
                e.stopPropagation();
                const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
                setBranchPopupPos({ top: rect.bottom + 4, left: rect.left });
                setBranchPopupId(branchPopupId === conv.conversation_id ? null : conv.conversation_id);
              }}
              title={`${branchMap.get(conv.conversation_id)?.length || conv.branch_count} ${t('chat.branches')}`}
            >
              <svg viewBox="0 0 24 24" width="10" height="10" fill="none" stroke="currentColor" strokeWidth="2.5">
                <circle cx="12" cy="18" r="3" />
                <circle cx="6" cy="6" r="3" />
                <circle cx="18" cy="6" r="3" />
                <path d="M18 9v1a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2V9" />
                <line x1="12" y1="12" x2="12" y2="15" />
              </svg>
              <span>{branchMap.get(conv.conversation_id)?.length || conv.branch_count}</span>
            </button>
          )}
          <div className={`conversation-menu-container ${menuOpenId === conv.conversation_id ? 'menu-open' : ''}`}>
            <button
              className="conversation-menu-btn"
              onClick={(e) => handleMenuToggle(e, conv.conversation_id)}
              title={t('sidebar.options')}
            >
              <svg viewBox="0 0 24 24" width="16" height="16">
                <circle cx="5" cy="12" r="1.5" fill="currentColor" />
                <circle cx="12" cy="12" r="1.5" fill="currentColor" />
                <circle cx="19" cy="12" r="1.5" fill="currentColor" />
              </svg>
            </button>
            {menuOpenId === conv.conversation_id && (
              <div ref={menuDropdownRef} className="conversation-menu-dropdown" style={{ top: menuPosition.top, left: Math.max(8, menuPosition.left) }}>
                {/* Routes are tenant-prefixed (`/team-alpha/chat`), so the old
                    `startsWith('/chat')` test never matched and this option was
                    effectively dead. Match the /chat SEGMENT instead. */}
                {activeConversationId && activeConversationId !== conv.conversation_id && /(^|\/)chat(\/|$|\?)/.test(location.pathname) && (
                  <button
                    className="conversation-menu-item"
                    onClick={(e) => {
                      e.stopPropagation();
                      setMenuOpenId(null);
                      window.dispatchEvent(new CustomEvent('openSplitScreen', {
                        detail: { conversationId: conv.conversation_id }
                      }));
                    }}
                  >
                    <svg viewBox="0 0 24 24" width="14" height="14">
                      <rect x="2" y="3" width="20" height="18" rx="2" stroke="currentColor" strokeWidth="2" fill="none" />
                      <line x1="12" y1="3" x2="12" y2="21" stroke="currentColor" strokeWidth="2" />
                    </svg>
                    {t('chat.openInSplitScreen')}
                  </button>
                )}
                <button
                  className="conversation-menu-item"
                  onClick={(e) => handleStartRename(e, conv)}
                >
                  <svg viewBox="0 0 24 24" width="14" height="14">
                    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" stroke="currentColor" strokeWidth="2" fill="none" />
                    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" stroke="currentColor" strokeWidth="2" fill="none" />
                  </svg>
                  {t('vault.rename')}
                </button>
                <ConversationExportMenuItems conversationId={conv.conversation_id}
                                             onDone={() => setMenuOpenId(null)} />
                <button
                  className="conversation-menu-item delete"
                  onClick={(e) => handleOpenDeleteModal(e, conv)}
                >
                  <svg viewBox="0 0 24 24" width="14" height="14">
                    <polyline points="3 6 5 6 21 6" stroke="currentColor" strokeWidth="2" fill="none" />
                    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" stroke="currentColor" strokeWidth="2" fill="none" />
                  </svg>
                  {t('vault.delete')}
                </button>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );

  const formatBranchDate = (dateStr: string) => formatRelativeTime(dateStr);

  // ===== Build branch maps for rendering =====
  const { branchMap, topLevelConvs } = useMemo(() => {
    const allIds = new Set(conversations.map((c: any) => c.conversation_id));
    const bMap = new Map<string, any[]>();
    const hidden = new Set<string>();
    conversations.forEach((c: any) => {
      if (c.parent_conversation_id && allIds.has(c.parent_conversation_id)) {
        const existing = bMap.get(c.parent_conversation_id) || [];
        existing.push(c);
        bMap.set(c.parent_conversation_id, existing);
        hidden.add(c.conversation_id);
      }
    });
    let topLevel = conversations.filter((c: any) => !hidden.has(c.conversation_id));
    // Inline panel search — filter by title / chatbot name (case-insensitive)
    const q = panelSearchQuery.trim().toLowerCase();
    if (q) {
      topLevel = topLevel.filter((c: any) =>
        (c.conversation_title || '').toLowerCase().includes(q) ||
        (c.chatbot_name || '').toLowerCase().includes(q)
      );
    }
    return { branchMap: bMap, hiddenBranchIds: hidden, topLevelConvs: topLevel };
  }, [conversations, panelSearchQuery]);

  // ===== Render branch popup (shared between views) =====
  const renderBranchPopup = (activeBranchMap: Map<string, any[]> = branchMap) => {
    if (!branchPopupId || !activeBranchMap.get(branchPopupId)) return null;

    return (
      <div
        className="branch-popup"
        style={{ top: branchPopupPos.top, left: branchPopupPos.left }}
      >
        <div className="branch-popup-header">
          <svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" strokeWidth="2.5">
            <circle cx="12" cy="18" r="3" />
            <circle cx="6" cy="6" r="3" />
            <circle cx="18" cy="6" r="3" />
            <path d="M18 9v1a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2V9" />
            <line x1="12" y1="12" x2="12" y2="15" />
          </svg>
          <span>{t('chat.branches')}</span>
        </div>
        <div className="branch-popup-list">
          {activeBranchMap.get(branchPopupId)!.map((branch: any) => (
            <div
              key={branch.conversation_id}
              className={`branch-popup-item ${isActiveConversation(branch.conversation_id) ? 'active' : ''}`}
              onClick={() => {
                if (renamingId === branch.conversation_id) return;
                setBranchPopupId(null);
                closeMobileSidebar();
                navigate(tenantPath(`/chat?conversation=${branch.conversation_id}`));
              }}
            >
              <div className="branch-popup-item-content">
                {renamingId === branch.conversation_id ? (
                  <input dir="auto"
                    ref={renameInputRef}
                    type="text"
                    className="conversation-rename-input"
                    value={renameValue}
                    onChange={(e) => setRenameValue(e.target.value)}
                    onBlur={() => handleRenameSubmit(branch.conversation_id)}
                    onKeyDown={(e) => handleRenameKeyDown(e, branch.conversation_id)}
                    onClick={(e) => e.stopPropagation()}
                    autoFocus
                  />
                ) : (
                  <>
                    <div dir="auto" className="branch-popup-item-title">
                      {/* Sibling branches of one thread share a title, so for a
                          handoff the agent is the only thing that tells them
                          apart. Shown as a dot + name, matching how a normal
                          conversation row identifies its agent. */}
                      {branch.branch_kind === 'agent' && branch.chatbot_name && (
                        <span
                          className="branch-popup-item-agent-dot"
                          style={{ background: getRecentChatDotColor(branch, botColorById) }}
                          title={branch.chatbot_name}
                        />
                      )}
                      {branch.conversation_title || t('chat.untitledBranch')}
                    </div>
                    <div className="branch-popup-item-date">
                      {branch.branch_kind === 'agent' && branch.chatbot_name && (
                        <span className="branch-popup-item-agent" dir="auto">{branch.chatbot_name}</span>
                      )}
                      {formatBranchDate(branch.last_message_at || branch.created_at)}
                    </div>
                  </>
                )}
              </div>
              {renamingId !== branch.conversation_id && (
                <button
                  className="branch-popup-item-menu"
                  onClick={(e) => {
                    e.stopPropagation();
                    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
                    setMenuPosition({ top: rect.bottom + 4, left: rect.right - 160 });
                    setMenuOpenId(menuOpenId === branch.conversation_id ? null : branch.conversation_id);
                  }}
                  title={t('sidebar.options')}
                >
                  <svg viewBox="0 0 24 24" width="14" height="14">
                    <circle cx="5" cy="12" r="1.5" fill="currentColor" />
                    <circle cx="12" cy="12" r="1.5" fill="currentColor" />
                    <circle cx="19" cy="12" r="1.5" fill="currentColor" />
                  </svg>
                </button>
              )}
              {menuOpenId === branch.conversation_id && (
                <div ref={menuDropdownRef} className="conversation-menu-dropdown" style={{ top: menuPosition.top, left: Math.max(8, menuPosition.left) }}>
                  {activeConversationId && activeConversationId !== branch.conversation_id && /(^|\/)chat(\/|$|\?)/.test(location.pathname) && (
                    <button
                      className="conversation-menu-item"
                      onClick={(e) => {
                        e.stopPropagation();
                        setMenuOpenId(null);
                        setBranchPopupId(null);
                        window.dispatchEvent(new CustomEvent('openSplitScreen', {
                          detail: { conversationId: branch.conversation_id }
                        }));
                      }}
                    >
                      <svg viewBox="0 0 24 24" width="14" height="14">
                        <rect x="2" y="3" width="20" height="18" rx="2" stroke="currentColor" strokeWidth="2" fill="none" />
                        <line x1="12" y1="3" x2="12" y2="21" stroke="currentColor" strokeWidth="2" />
                      </svg>
                      {t('chat.openInSplitScreen')}
                    </button>
                  )}
                  <button
                    className="conversation-menu-item"
                    onClick={(e) => {
                      e.stopPropagation();
                      setMenuOpenId(null);
                      setRenamingId(branch.conversation_id);
                      setRenameValue(branch.conversation_title || '');
                      setTimeout(() => renameInputRef.current?.select(), 0);
                    }}
                  >
                    <svg viewBox="0 0 24 24" width="14" height="14">
                      <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" stroke="currentColor" strokeWidth="2" fill="none" />
                      <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" stroke="currentColor" strokeWidth="2" fill="none" />
                    </svg>
                    {t('vault.rename')}
                  </button>
                  <ConversationExportMenuItems conversationId={branch.conversation_id}
                                               onDone={() => { setMenuOpenId(null); setBranchPopupId(null); }} />
                  <button
                    className="conversation-menu-item delete"
                    onClick={(e) => {
                      e.stopPropagation();
                      setBranchPopupId(null);
                      handleOpenDeleteModal(e, branch);
                    }}
                  >
                    <svg viewBox="0 0 24 24" width="14" height="14">
                      <polyline points="3 6 5 6 21 6" stroke="currentColor" strokeWidth="2" fill="none" />
                      <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" stroke="currentColor" strokeWidth="2" fill="none" />
                    </svg>
                    {t('vault.delete')}
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    );
  };

  // ===== Render: Flat view =====
  const renderFlatView = () => (
    <>
      {topLevelConvs.map((conv: any) => (
        <div key={conv.conversation_id} className="conversation-group">
          {renderConversationItem(conv, !!conv.parent_conversation_id, branchMap)}
        </div>
      ))}
      {renderBranchPopup()}
    </>
  );

  // ===== Render: Grouped view (lazy-loaded per agent) =====
  const renderGroupedView = () => {
    if (agentListLoading && agentList.length === 0) {
      return (
        <div className="scroll-sentinel" style={{ padding: '16px 0' }}>
          <div className="scroll-sentinel-spinner">
            <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M21 12a9 9 0 1 1-6.219-8.56" />
            </svg>
          </div>
        </div>
      );
    }

    // Build a branch map from agentConvos data for grouped view
    const groupedBranchMap = new Map<string, any[]>();
    const groupedHiddenIds = new Set<string>();
    agentConvos.forEach((data) => {
      const allIds = new Set(data.conversations.map((c: any) => c.conversation_id));
      data.conversations.forEach((c: any) => {
        if (c.parent_conversation_id && allIds.has(c.parent_conversation_id)) {
          const existing = groupedBranchMap.get(c.parent_conversation_id) || [];
          existing.push(c);
          groupedBranchMap.set(c.parent_conversation_id, existing);
          groupedHiddenIds.add(c.conversation_id);
        }
      });
    });

    return (
      <>
        {agentList.map((agent) => {
          const agentKey = agent.chatbot_id || 'general';
          const isOpen = openGroups.has(agentKey);
          const groupData = agentConvos.get(agentKey);
          const topLevelGroupConvs = groupData?.conversations.filter((c: any) => !groupedHiddenIds.has(c.conversation_id)) || [];

          return (
            <div key={agentKey} className="conversation-group-container">
              {/* Group Header */}
              <div
                className="conversation-group-header"
                onClick={() => {
                  setOpenGroups(prev => {
                    const next = new Set(prev);
                    if (next.has(agentKey)) {
                      next.delete(agentKey);
                    } else {
                      next.add(agentKey);
                      // Fetch conversations on first open
                      if (!groupData?.loaded && !groupData?.loading) {
                        fetchAgentConversations(agentKey, agent.chatbot_id, 1);
                      }
                    }
                    return next;
                  });
                }}
              >
                <div className="conversation-group-header-left">
                  {agent.chatbot_id ? (
                    <BotAvatar name={agent.chatbot_name || '??'} size={22} colorOverride={(agent as any).avatar_color} variantOverride={(agent as any).avatar_variant ?? undefined} />
                  ) : (
                    <span className="group-bot-avatar group-bot-avatar-general">
                      <svg viewBox="0 0 24 24" width="11" height="11" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
                      </svg>
                    </span>
                  )}
                  <span dir="auto" className="group-name">{agent.chatbot_name || t('sidebar.generalAI')}</span>
                </div>
                <svg className={`group-chevron ${!isOpen ? 'collapsed' : ''}`} viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="6 9 12 15 18 9" />
                </svg>
              </div>

              {/* Group Content - lazy loaded */}
              {isOpen && (
                <div className="conversation-group-content">
                  {groupData?.loading && !groupData.loaded ? (
                    <div className="conversation-group-show-more" style={{ justifyContent: 'center', cursor: 'default' }}>
                      <span className="loading-dots">...</span>
                    </div>
                  ) : topLevelGroupConvs.length > 0 ? (
                    <>
                      {/* Force every conversation under this group to the
                          agent's color. agentList doesn't carry avatar_color,
                          so we resolve it via the bots lookup. */}
                      {(() => {
                        // Resolve the group's agent color via multiple fallbacks:
                        // 1. Bots lookup by id  (live, authoritative)
                        // 2. agent.avatar_color (payload from /agents endpoint)
                        // 3. First conversation's cached avatar_color
                        const firstConv: any = topLevelGroupConvs[0];
                        const groupColor =
                          (agent.chatbot_id && botColorById[agent.chatbot_id]?.color) ||
                          agent.avatar_color ||
                          (firstConv && firstConv.avatar_color) ||
                          'blue';
                        return topLevelGroupConvs.map((conv: any) =>
                          renderConversationItem(
                            conv,
                            !!conv.parent_conversation_id,
                            groupedBranchMap,
                            groupColor,
                          )
                        );
                      })()}

                      {/* Load more conversations for this agent */}
                      {groupData?.hasNext && (
                        <button
                          className="conversation-group-show-more"
                          onClick={(e) => {
                            e.stopPropagation();
                            fetchAgentConversations(agentKey, agent.chatbot_id, (groupData?.page || 1) + 1);
                          }}
                          disabled={groupData?.loading}
                        >
                          {groupData?.loading ? (
                            <span className="loading-dots">...</span>
                          ) : (
                            <>
                              {t('sidebar.showMore')}
                              <svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <polyline points="6 9 12 15 18 9" />
                              </svg>
                            </>
                          )}
                        </button>
                      )}
                    </>
                  ) : groupData?.loaded ? (
                    <div className="conversation-item" style={{ opacity: 0.5, fontSize: '12px' }}>{t('dashboard.noRecentConversations')}</div>
                  ) : null}
                </div>
              )}
            </div>
          );
        })}
        {renderBranchPopup(groupedBranchMap)}
      </>
    );
  };

  return (
    <>
      {/* Mobile menu button — floating, elegant, icon-only (replaces the old mobile header) */}
      <button
        className="mobile-menu-fab"
        onClick={handleMobileMenuToggle}
        aria-label={t('sidebar.openMenu')}
      >
        <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
          <line x1="4" y1="8" x2="20" y2="8" />
          <line x1="4" y1="16" x2="14" y2="16" />
        </svg>
      </button>

      {/* Mobile Overlay */}
      <div
        className={`sidebar-overlay ${isMobileOpen ? 'visible' : ''}`}
        onClick={handleOverlayClick}
      />

      {/* ========= Sidebar — collapsed rail by default, expands on hover (desktop) ========= */}
      <aside
        className={`sidebar sidebar-rail ${isPanelCollapsed ? 'collapsed' : ''} ${isMobileOpen ? 'mobile-open' : ''}`}
        id="sidebar"
        onMouseEnter={handleSidebarMouseEnter}
        onMouseLeave={handleSidebarMouseLeave}
      >
        {/* Sidebar header — always the compact mark, never the full wordmark.
            `railIcon` swaps between dark-on-light and white-on-dark assets
            based on the active theme. */}
        <div className="sidebar-header">
          <img
            src={railIcon}
            alt={brand.name}
            className="logo-image logo-image-compact"
            onClick={handleNavigateHome}
            title={brand.name}
          />
        </div>

      {/* Scrollable Content Area (rail only holds nav) */}
      <div className="sidebar-content sidebar-rail-content">
      {/* Navigation Menu - Compact GPT-style */}
      <nav className="nav-menu">
        {/* Home and Agents are agent-module entries — only show when modules.agents is on. */}
        {defaultOptions.showHome && agentsEnabled && (
          <div
            className={`nav-item ${activeItem === 'home' ? 'active' : ''}`}
            data-testid="nav-home"
            onClick={handleNavigateHome}
            data-tooltip={t('nav.home')}
          >
            {icons.home}
            <span className="nav-text">{t('nav.home')}</span>
          </div>
        )}

        {defaultOptions.showBots && agentsEnabled && (
          <div
            className={`nav-item ${activeItem === 'bots' ? 'active' : ''}`}
            data-testid="nav-agents"
            onClick={handleNavigateBots}
            data-tooltip={t('nav.agents')}
          >
            {icons.bots}
            <span className="nav-text">{t('nav.agents')}</span>
          </div>
        )}

        {/* Projects — shared workspaces. Every role sees it when the tenant has
            the flag on: a plain user can create a project and invite colleagues
            inside their own tenant, so this is deliberately not role-gated. */}
        {projectsEnabled && (
          <div
            className={`nav-item ${activeItem === 'projects' ? 'active' : ''}`}
            data-testid="nav-projects"
            onClick={handleOpenProjects}
            data-tooltip={t('nav.projects') || 'Projects'}
          >
            {icons.projects}
            <span className="nav-text">{t('nav.projects') || 'Projects'}</span>
          </div>
        )}

        {/* Flows module — visible only when the tenant has the modules.flows flag on. */}
        {flowsEnabled && (
          <div
            className={`nav-item ${activeItem === 'flows' ? 'active' : ''}`}
            data-testid="nav-flows"
            onClick={handleNavigateFlows}
            data-tooltip={t('nav.flows')}
          >
            {icons.flows}
            <span className="nav-text">{t('nav.flows')}</span>
          </div>
        )}

        {/* Insights — visible to group_admin and super_admin, gated on
            per-tenant `modules.insights`. (Swapped above Admin per design.) */}
        {isGroupAdmin && insightsEnabled && (
          <div
            className={`nav-item ${activeItem === 'insights' ? 'active' : ''}`}
            data-testid="nav-insights"
            onClick={handleOpenInsights}
            data-tooltip={t('insights.title')}
          >
            {icons.insights}
            <span className="nav-text">{t('insights.title')}</span>
          </div>
        )}

        {/* The Library — nested file system, Drive-style. Admin roles only
            (operator decision 2026-08-24, reversing 2026-08-19's every-member
            opening): plain users have no Library surface at all. What is
            shared still GRANTS them rights — agents and other surfaces can
            act on it — but the Library itself is administered, not browsed,
            by them. The backend refuses the role too; this hides the door. */}
        {libraryEnabled && isAdmin && (
          <div
            className={`nav-item ${activeItem === 'vault' ? 'active' : ''}`}
            data-testid="nav-library"
            onClick={handleOpenVault}
            data-tooltip={t('nav.vault')}
          >
            {icons.vault}
            <span className="nav-text">{t('nav.vault')}</span>
          </div>
        )}

        {/* Administration */}
        {defaultOptions.showAdmin && isAdmin && (
          <div
            className={`nav-item admin-nav ${activeItem === 'admin' ? 'active' : ''}`}
            data-testid="nav-admin"
            onClick={handleOpenAdmin}
            data-tooltip={t('nav.admin')}
          >
            {icons.admin}
            <span className="nav-text">{t('nav.admin')}</span>
          </div>
        )}

        {/* Moved out of the sidebar into tabbed pages (routes still resolve):
            Skills + Data Bundles + Agent of Agents → Agents page tabs;
            Cost Simulator → Settings tab; Design System → Super Admin tab. */}

        {/* Super Admin / Design System — visible only to a super_admin
            AND only while they're browsing their OWN home tenant URL.
            When a super_admin is visiting a customer tenant (e.g.
            /team-alpha/), these items hide so the customer's brand is
            never associated with platform-admin tools. Their handlers
            navigate to /<home-tenant>/super-admin if surfaced. */}
        {isSuperAdmin && onSuperAdminHomeTenant && (
          <div
            className={`nav-item admin-nav super-admin-nav ${activeItem === 'superadmin' ? 'active' : ''}`}
            data-testid="nav-superadmin"
            onClick={handleOpenSuperAdmin}
            data-tooltip={t('sidebar.superAdmin')}
          >
            {icons.star}
            <span className="nav-text">{t('sidebar.superAdmin')}</span>
          </div>
        )}

        {/* Design System moved INTO the Super Admin page as a tab.
            See SuperAdminPage.tsx. Route /design-system still resolves. */}

        {/* Custom Nav Items */}
        {defaultOptions.customNavItems?.map((item) => {
          if (item.requireSuperAdmin && !isSuperAdmin) return null;
          if (item.requireAdmin && !isAdmin) return null;

          return (
            <div
              key={item.id}
              className={`nav-item ${item.active ? 'active' : ''} ${item.customClass || ''}`}
              onClick={item.action}
              data-tooltip={item.tooltip}
            >
              {item.icon}
              <span className="nav-text" dir="auto">{item.label}</span>
            </div>
          );
        })}
      </nav>

      {/* Divider between nav icons and Recent Chats — hidden when the sidebar
          is collapsed and when the agents module is off (Recent Chats and the
          Bots list are agent-module surfaces). */}
      {((defaultOptions.showConversations && agentsEnabled) ||
        (defaultOptions.customListItems && defaultOptions.customListItems.length > 0) ||
        (defaultOptions.showBotsList && agentsEnabled)) && (
        <div className="sidebar-divider" aria-hidden="true" />
      )}

      {/* Custom List Section (caller-provided rows, e.g. Recent Flows on the flows dashboard) */}
      {defaultOptions.customListItems && defaultOptions.customListItems.length > 0 && (
        <div className="conversations-section">
          <div className="conversations-header">
            <div className="conversations-title">{defaultOptions.customListTitle || 'Recent'}</div>
          </div>
          <div className="conversations-list">
            {defaultOptions.customListItems.map((item) => (
              <div
                key={item.id}
                className={`conversation-item ${item.active ? 'active' : ''}`}
                onClick={item.onClick}
                title={item.sublabel || item.label}
              >
                <div className="conversation-icon">
                  <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z"/>
                  </svg>
                </div>
                <div className="conversation-info">
                  <span className="conversation-title" dir="auto">{item.label}</span>
                  {item.sublabel && (
                    <span className="conversation-preview">{item.sublabel}</span>
                  )}
                </div>
                {item.onDelete && (
                  <button
                    className="conversation-menu-btn"
                    onClick={(e) => { e.stopPropagation(); item.onDelete!(); }}
                    title={t('common.delete')}
                  >
                    <svg viewBox="0 0 24 24" width="14" height="14">
                      <polyline points="3 6 5 6 21 6" stroke="currentColor" strokeWidth="2" fill="none" />
                      <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" stroke="currentColor" strokeWidth="2" fill="none" />
                    </svg>
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Recent Conversations — agents and flows mixed; per-row icons differentiate.
          Hidden when modules.agents is off (no chats can be created). */}
      {defaultOptions.showConversations && agentsEnabled && (
        <div className="conversations-section conversations-section-main">
          <div className="conversations-header">
            <div className="conversations-title">{t('dashboard.recentChats')}</div>
            <div className="conversations-header-actions">
              {/* Inline search toggle (stays inside the panel) */}
              {defaultOptions.showSearch && (
                <button
                  className={`conversations-view-toggle ${panelSearchOpen ? 'active' : ''}`}
                  onClick={() => {
                    setPanelSearchOpen(prev => {
                      if (prev) setPanelSearchQuery('');
                      return !prev;
                    });
                  }}
                  title={t('nav.search')}
                >
                  <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                    <circle cx="11" cy="11" r="7" />
                    <path d="M21 21l-4.35-4.35" />
                  </svg>
                </button>
              )}
              {/* View toggle: flat ↔ grouped */}
              <button
                className="conversations-view-toggle"
                onClick={() => setViewMode(prev => prev === 'flat' ? 'grouped' : 'flat')}
                title={viewMode === 'flat' ? t('sidebar.groupByAgent') : t('sidebar.chronologicalView')}
              >
              {viewMode === 'flat' ? (
                // Group icon - switch to grouped view
                <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="3" y="3" width="7" height="7" rx="1" />
                  <rect x="14" y="3" width="7" height="7" rx="1" />
                  <rect x="3" y="14" width="7" height="7" rx="1" />
                  <rect x="14" y="14" width="7" height="7" rx="1" />
                </svg>
              ) : (
                // List icon - switch to flat view
                <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="8" y1="6" x2="21" y2="6" />
                  <line x1="8" y1="12" x2="21" y2="12" />
                  <line x1="8" y1="18" x2="21" y2="18" />
                  <line x1="3" y1="6" x2="3.01" y2="6" />
                  <line x1="3" y1="12" x2="3.01" y2="12" />
                  <line x1="3" y1="18" x2="3.01" y2="18" />
                </svg>
              )}
            </button>
            </div>
          </div>

          {/* Inline search input — stays inside the panel */}
          {panelSearchOpen && (
            <div className="panel-search-bar">
              <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" className="panel-search-icon">
                <circle cx="11" cy="11" r="7" />
                <path d="M21 21l-4.35-4.35" />
              </svg>
              <input dir="auto"
                type="text"
                className="panel-search-input"
                placeholder={t('nav.search')}
                value={panelSearchQuery}
                onChange={(e) => setPanelSearchQuery(e.target.value)}
                autoFocus
              />
              {panelSearchQuery && (
                <button
                  className="panel-search-clear"
                  onClick={() => setPanelSearchQuery('')}
                  title={t('sidebar.clear')}
                >
                  <svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                    <line x1="18" y1="6" x2="6" y2="18" />
                    <line x1="6" y1="6" x2="18" y2="18" />
                  </svg>
                </button>
              )}
            </div>
          )}

          <div className="conversations-list" id="recentConversations">
            {conversations.length === 0 ? (
              <div className="conversation-item">{t('dashboard.noRecentConversations')}</div>
            ) : viewMode === 'flat' ? (
              renderFlatView()
            ) : (
              renderGroupedView()
            )}

            {/* Infinite scroll sentinel (flat: more conversations, grouped: more agents) */}
            {((viewMode === 'flat' && globalHasNext) || (viewMode === 'grouped' && agentHasNext)) && (
              <div ref={scrollSentinelRef} className="scroll-sentinel">
                {(isLoadingMore || agentListLoading) && (
                  <div className="scroll-sentinel-spinner">
                    <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M21 12a9 9 0 1 1-6.219-8.56" />
                    </svg>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Bots List — agent-module surface. */}
      {defaultOptions.showBotsList && agentsEnabled && (
        <div className="bots-section">
          <div className="bots-header">
            <div className="bots-title">{t('sidebar.agents')}</div>
            <button
              className="bots-manage-btn"
              onClick={() => navigate(tenantPath('/agents'))}
              title={t('sidebar.manageAgents')}
            >
              {icons.filter}
            </button>
          </div>
          <div className="bots-list" id="sidebarBots">
            {bots.length === 0 ? (
              <div className="bot-item">
                <div className="bot-item-avatar">...</div>
                <span dir="auto" className="bot-item-name">{t('sidebar.noAgents')}</span>
              </div>
            ) : (
              bots.slice(0, 5).map((bot: any) => (
                <div
                  key={bot.chatbot_id}
                  className="bot-item"
                  onClick={() => {
                    closeMobileSidebar();
                    navigate(tenantPath(`/chat?bot=${bot.chatbot_id}`));
                  }}
                >
                  <div className="bot-item-avatar">
                    {bot.chatbot_name.charAt(0).toUpperCase()}
                  </div>
                  <span className="bot-item-name" dir="auto">{bot.chatbot_name}</span>
                </div>
              ))
            )}
          </div>
        </div>
      )}
      </div>
      {/* End of Rail scrollable content */}

      {/* Bottom area — help, support, then the user entry (opens Settings).
          The sidebar expand/collapse is driven by hover on desktop. */}
      <div className="rail-actions">
        {/* Help & tutorials — the door to the Learn page. Only where the
            guided onboarding is on for this tenant; a tenant without it
            never sees that such a page exists. */}
        {journeyEnabled && agentsEnabled && (
          <button
            className={`rail-help-entry ${activeItem === 'learn' ? 'active' : ''}`}
            onClick={() => navigate(tenantPath('/learn'))}
            title={t('nav.help')}
            data-tooltip={t('nav.help')}
            data-testid="rail-help-entry"
          >
            <span className="rail-help-icon" aria-hidden="true">?</span>
            <span className="rail-help-name">{t('nav.help')}</span>
          </button>
        )}
        {/* Support and Feedback — directly above the avatar, a bare icon in the
            text colour (it used to be a 16px grey glyph in the viewport corner,
            and people missed it). */}
        <button
          type="button"
          className="rail-support-entry"
          onClick={openSupport}
          title={t('sidebar.supportAndFeedback')}
          data-tooltip={t('sidebar.supportAndFeedback')}
          data-testid="rail-support-entry"
        >
          <span className="rail-support-icon" aria-hidden="true">
            <SupportIcon />
          </span>
          <span className="rail-support-name">{t('sidebar.supportAndFeedback')}</span>
        </button>
        <button
          id="userMenu"
          className="rail-user-entry"
          onClick={() => navigate(tenantPath('/settings'))}
          title={t('nav.userSettings')}
          data-tooltip={t('nav.userSettings')}
        >
          <span className="rail-user-avatar" aria-hidden="true">
            {getFirstInitial(user?.full_name || user?.user_full_name || user?.email || 'U')}
          </span>
          <span dir="auto" className="rail-user-name">
            {capitalizeName(user?.full_name || user?.user_full_name || user?.email?.split('@')[0] || 'User')}
          </span>
        </button>

        {/* Powered by Brij — quiet attribution / signature on every tenant.
            This is INTENTIONALLY not white-labeled — every tenant sees the
            Brij mark here regardless of their brand_overrides. Hidden in
            the collapsed rail (via CSS) so the rail stays minimalist. */}
        <a
          className="rail-powered-by"
          href="https://brijlabs.ai/"
          target="_blank"
          rel="noopener noreferrer"
          aria-label={`${t('common.poweredBy')} Brij`}
        >
          <span className="pb-text">
            {t('common.poweredBy')} <span className="pb-brand">Brij</span>
          </span>
          <img src={brijIconBlack} alt="" className="pb-icon" aria-hidden="true" />
        </a>
        {/* The platform's documents, beside the platform's signature — the
            same on every tenant, like the attribution above. */}
        {PUBLIC_DOCUMENTS_PUBLISHED && (
          <nav className="rail-legal-links" aria-label={t('legal.nav.label')}>
            <a href="/privacy">{t('legal.nav.privacy')}</a>
            <span aria-hidden="true">·</span>
            <a href="/terms">{t('legal.nav.terms')}</a>
          </nav>
        )}
      </div>

      </aside>
      {/* End of Sidebar */}

      {/* Delete Conversation Confirmation Modal */}
      <ConfirmModal
        isOpen={deleteModal.isOpen}
        onClose={() => setDeleteModal({ isOpen: false, conversationId: '', title: '', loading: false, branchCount: 0 })}
        onConfirm={handleDeleteConversation}
        title={t('sidebar.deleteConversation')}
        message={
          deleteModal.branchCount > 0
            ? t('sidebar.deleteConversationWithBranchesConfirm', {
              title: bidiIsolate(deleteModal.title), count: deleteModal.branchCount,
            })
            : t('sidebar.deleteConversationConfirm', { title: bidiIsolate(deleteModal.title) })
        }
        confirmText={t('common.delete')}
        cancelText={t('common.cancel')}
        variant="danger"
        loading={deleteModal.loading}
      />
    </>
  );
};
